/**
 * Component 2&3 Combined: Current/Target Condition
 * Side-by-side display of Current Condition and Target Condition with shared factors
 * Receives outcomes from Component 1 and creates factors for visualization
 */

import React, { useState, useEffect } from 'react';
import { 
  Edit as EditIcon, 
  Plus as PlusIcon, 
  X as XIcon,
  Trash2 as TrashIcon,
  Target as TargetIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ArrowRight as ArrowRightIcon
} from 'lucide-react';
import type { KATABoardV2, Outcome, Factor } from '../../../types/kataBoardV2';
import { useKataBoardV2Store } from '../../../stores/kataBoardV2Store';
import { useAlert } from '../../../hooks/useAlert';
import { useKataBoardCanEdit } from '../../../contexts/KataBoardAccessContext';

interface Component02_03Props {
  kataBoard: KATABoardV2;
}

interface NewFactor {
  name: string;
  description: string;
  current_value: string;
  target_value: string;
  kpi_type: 'increase' | 'decrease';
}

const Component02_03_CurrentTargetCondition: React.FC<Component02_03Props> = ({ kataBoard }) => {
  const { 
    createFactor, 
    updateFactor, 
    deleteFactor,
    updateOutcome,
    deleteOutcome,
    componentState,
    setEditing,
    setUnsavedChanges,
    fetchKataBoardByWorkshop
  } = useKataBoardV2Store();
  
  const { confirmDelete, AlertComponent } = useAlert();
  const canEdit = useKataBoardCanEdit();
  
  const [isEditingLocal, setIsEditingLocal] = useState(false);
  const [outcomes, setOutcomes] = useState<Outcome[]>([]);
  const [showAddFactor, setShowAddFactor] = useState<number | null>(null);
  const [editingOutcome, setEditingOutcome] = useState<number | null>(null);
  const [editingFactor, setEditingFactor] = useState<number | null>(null);
  const [newFactor, setNewFactor] = useState<NewFactor>({
    name: '',
    description: '',
    current_value: '',
    target_value: '',
    kpi_type: 'increase'
  });
  const [editingOutcomeData, setEditingOutcomeData] = useState({
    current_value: '',
    target_value: ''
  });
  const [editingFactorData, setEditingFactorData] = useState({
    name: '',
    description: '',
    current_value: '',
    target_value: '',
    kpi_type: 'increase' as 'increase' | 'decrease'
  });

  useEffect(() => {
    setIsEditingLocal(canEdit && componentState.isEditing);
  }, [canEdit, componentState.isEditing]);

  useEffect(() => {
    // Get all outcomes from the KATA board (created in Component 1)
    const allOutcomes = kataBoard.outcomes?.filter(
      outcome => outcome.outcome_type === 'current'
    ) || [];
    setOutcomes(allOutcomes);
  }, [kataBoard.outcomes]);

  const handleEdit = () => {
    if (!canEdit) return;
    setIsEditingLocal(true);
    setEditing(true);
  };

  const handleCancel = () => {
    setIsEditingLocal(false);
    setEditing(false);
    setShowAddFactor(null);
    setEditingOutcome(null);
    setEditingFactor(null);
    resetNewFactor();
  };

  const handleEditOutcome = (outcome: Outcome) => {
    setEditingOutcome(outcome.id);
    setEditingOutcomeData({
      current_value: outcome.current_value?.toString() || '',
      target_value: outcome.target_value?.toString() || ''
    });
  };

  const handleSaveOutcome = async (outcomeId: number) => {
    try {
      await updateOutcome(outcomeId, {
        current_value: editingOutcomeData.current_value ? parseFloat(editingOutcomeData.current_value) : null,
        target_value: editingOutcomeData.target_value ? parseFloat(editingOutcomeData.target_value) : null
      });
      setEditingOutcome(null);
      await fetchKataBoardByWorkshop(kataBoard.workshop);
    } catch (error) {
      console.error('Failed to update outcome:', error);
    }
  };

  const handleCancelOutcomeEdit = () => {
    setEditingOutcome(null);
    setEditingOutcomeData({ current_value: '', target_value: '' });
  };

  const handleEditFactor = (factor: any) => {
    setEditingFactor(factor.id);
    setEditingFactorData({
      name: factor.name || '',
      description: factor.description || '',
      current_value: factor.current_value?.toString() || '',
      target_value: factor.target_value?.toString() || '',
      kpi_type: factor.kpi_type || 'increase'
    });
  };

  const handleSaveFactor = async (factorId: number) => {
    try {
      await updateFactor(factorId, {
        name: editingFactorData.name,
        description: editingFactorData.description,
        current_value: editingFactorData.current_value,
        target_value: editingFactorData.target_value,
        kpi_type: editingFactorData.kpi_type
      });
      setEditingFactor(null);
      await fetchKataBoardByWorkshop(kataBoard.workshop);
    } catch (error) {
      console.error('Failed to update factor:', error);
    }
  };

  const handleCancelFactorEdit = () => {
    setEditingFactor(null);
    setEditingFactorData({ name: '', description: '', current_value: '', target_value: '', kpi_type: 'increase' });
  };

  const handleDeleteFactor = async (factorId: number, factorName: string) => {
    confirmDelete(
      `factor "${factorName}"`,
      async () => {
        try {
          await deleteFactor(factorId);
          await fetchKataBoardByWorkshop(kataBoard.workshop);
        } catch (error) {
          console.error('Failed to delete factor:', error);
        }
      }
    );
  };

  const resetNewFactor = () => {
    setNewFactor({
      name: '',
      description: '',
      current_value: '',
      target_value: '',
      kpi_type: 'increase'
    });
  };

  const handleAddFactor = async (outcomeId: number) => {
    try {
      await createFactor({
        outcome: outcomeId,
        name: newFactor.name,
        description: newFactor.description,
        current_value: newFactor.current_value,
        target_value: newFactor.target_value,
        kpi_type: newFactor.kpi_type,
        order: 0
      });
      resetNewFactor();
      setShowAddFactor(null);
      await fetchKataBoardByWorkshop(kataBoard.workshop);
    } catch (error) {
      console.error('Failed to create factor:', error);
    }
  };


  const getOutcomeColor = (outcomeIndex: number, isTarget: boolean = false) => {
    const baseColors = [
      { current: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-800', line: '#3B82F6' },
        target: { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-800', line: '#10B981' } },
      { current: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-800', line: '#8B5CF6' },
        target: { bg: 'bg-emerald-100', border: 'border-emerald-400', text: 'text-emerald-800', line: '#059669' } },
      { current: { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-800', line: '#F59E0B' },
        target: { bg: 'bg-teal-100', border: 'border-teal-400', text: 'text-teal-800', line: '#0D9488' } },
    ];
    const colorSet = baseColors[outcomeIndex % baseColors.length];
    return isTarget ? colorSet.target : colorSet.current;
  };

  const renderOutcomeSection = (outcome: Outcome, outcomeIndex: number, isTarget: boolean) => {
    const factors = outcome.factors || [];
    const outcomeColor = getOutcomeColor(outcomeIndex, isTarget);
    const sectionTitle = isTarget ? 'Target Condition' : 'Current Condition';
    const outcomeValue = isTarget ? outcome.target_value : outcome.current_value;
    
    return (
      <div className={`flex-1 bg-white border-2 ${outcomeColor.border} rounded-lg p-6`}>
        {/* Section Header */}
        <div className="text-center mb-6">
          <h3 className={`text-lg font-semibold ${outcomeColor.text} mb-2`}>
            {sectionTitle}
          </h3>
          <div className={`text-sm ${outcomeColor.text} opacity-75`}>
            {outcome.name}
          </div>
          {outcomeValue && (
            <div className={`text-sm font-semibold mt-1 ${isTarget ? 'text-green-600' : 'text-red-600'}`}>
              {isTarget ? 'Target: ' : 'Current: '}{outcomeValue} {outcome.unit}
            </div>
          )}
        </div>

        {/* Outcome Diagram */}
        <div className="relative h-96 w-full bg-white border border-gray-200 rounded-lg p-8">

          {/* Central Outcome Circle */}
          <div className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2" style={{ left: '35%', zIndex: 2 }}>
            <div 
              className={`w-40 h-40 rounded-full flex flex-col items-center justify-center shadow-lg`}
              style={{ 
                backgroundColor: isTarget ? '#10B981' : '#3B82F6',
                border: `4px solid ${isTarget ? '#059669' : '#1E40AF'}`
              }}
            >
              <div className="text-center text-white">
                <div className="text-base font-bold">
                  {isTarget ? 'Target' : 'Current'}
                </div>
                <div className="text-base font-bold">
                  Condition
                </div>
                <div className="text-sm font-medium mt-1">
                  {outcome.name}={outcomeValue ? `${outcomeValue}` : 'Value'}
                </div>
              </div>
            </div>
          </div>

          {/* Factor Circles in Arc Formation */}
          {factors.map((factor, factorIndex) => {
            const factorValue = isTarget ? factor.target_value : factor.current_value;
            
            // Position factors in arc around the central circle - shifted left
            const radius = 40; // Distance from center - increased for bigger diagram
            let angle;
            const totalFactors = factors.length;
            
            // Calculate angle spacing based on number of factors
            let angleSpacing;
            if (totalFactors === 3) angleSpacing = 60;
            else if (totalFactors === 4) angleSpacing = 45;
            else if (totalFactors === 5) angleSpacing = 36;
            else if (totalFactors === 6) angleSpacing = 30;
            else angleSpacing = 180 / Math.max(1, totalFactors - 1); // Default fallback
            
            // Position factors in right arc, centered around 0° (right side)
            const totalArcSpan = (totalFactors - 1) * angleSpacing;
            const arcCenter = 0; // Right side (3 o'clock position)
            const startAngle = arcCenter - (totalArcSpan / 2);
            
            angle = startAngle + (factorIndex * angleSpacing);
            
            const angleRad = (angle * Math.PI) / 180;
            const x = 35 + radius * Math.cos(angleRad); // Shifted left to 35%
            const y = 50 + radius * Math.sin(angleRad);

            return (
              <div
                key={factor.id}
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ 
                  left: `${x}%`, 
                  top: `${y}%`,
                  zIndex: 3 
                }}
              >
                <div className="relative group">
                  {/* Factor Circle */}
                  <div className="w-20 h-20 bg-white border-2 border-gray-400 rounded-full flex flex-col items-center justify-center shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer">
                    <div className="text-xs font-bold text-gray-800 text-center px-1">
                      {factor.name || `Factor ${factorIndex + 1}`}
                    </div>
                    <div className="text-xs text-gray-600 text-center">
                      {factorValue || 'Value'}
                    </div>
                  </div>

                  {/* Tooltip */}
                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-white border border-gray-200 rounded-lg p-2 shadow-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 min-w-32 z-30 pointer-events-none">
                    <div className="space-y-1 text-xs">
                      <div className="font-medium text-gray-900">{factor.name}</div>
                      {factor.description && (
                        <div className="text-gray-600">{factor.description}</div>
                      )}
                      <div className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                        factor.kpi_type === 'increase' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {factor.kpi_type === 'increase' ? '↑' : '↓'} {factor.kpi_type}
                      </div>
                      {factor.current_value && (
                        <div className="text-red-600">Current: {factor.current_value}</div>
                      )}
                      {factor.target_value && (
                        <div className="text-green-600">Target: {factor.target_value}</div>
                      )}
                    </div>
                    
                    {isEditingLocal && (
                      <div className="mt-1 pt-1 border-t border-gray-100 flex justify-center space-x-1 pointer-events-auto">
                        <button
                          onClick={() => handleEditFactor(factor)}
                          className="text-blue-500 hover:text-blue-700 p-1 rounded hover:bg-blue-50"
                          title="Edit factor"
                        >
                          <EditIcon className="h-3 w-3" />
                        </button>
                        <button
                          onClick={() => handleDeleteFactor(factor.id, factor.name)}
                          className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                          title="Delete factor"
                        >
                          <TrashIcon className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add Factor Form (if active for this outcome) */}
          {showAddFactor === outcome.id && (
            <div className="absolute bottom-4 left-4 right-4 p-3 bg-gray-50 rounded border border-gray-200 z-20">
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Factor name"
                  value={newFactor.name}
                  onChange={(e) => setNewFactor({ ...newFactor, name: e.target.value })}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                />
                <input
                  type="text"
                  placeholder="Current value"
                  value={newFactor.current_value}
                  onChange={(e) => setNewFactor({ ...newFactor, current_value: e.target.value })}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                />
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Target value"
                  value={newFactor.target_value}
                  onChange={(e) => setNewFactor({ ...newFactor, target_value: e.target.value })}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                />
                <select
                  value={newFactor.kpi_type}
                  onChange={(e) => setNewFactor({ ...newFactor, kpi_type: e.target.value as 'increase' | 'decrease' })}
                  className="px-2 py-1 border border-gray-300 rounded text-xs"
                >
                  <option value="increase">Increase (Higher is Better)</option>
                  <option value="decrease">Decrease (Lower is Better)</option>
                </select>
              </div>
              <div className="mb-2">
                <input
                  type="text"
                  placeholder="Description (optional)"
                  value={newFactor.description}
                  onChange={(e) => setNewFactor({ ...newFactor, description: e.target.value })}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs"
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={() => setShowAddFactor(null)}
                  className="px-2 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleAddFactor(outcome.id)}
                  disabled={!newFactor.name}
                  className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Add Factor
                </button>
              </div>
            </div>
          )}

          {/* Edit Factor Modal */}
          {editingFactor && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6 w-96 max-w-md">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Edit Factor</h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Factor Name</label>
                    <input
                      type="text"
                      placeholder="Factor name"
                      value={editingFactorData.name}
                      onChange={(e) => setEditingFactorData({ ...editingFactorData, name: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <textarea
                      placeholder="Description (optional)"
                      value={editingFactorData.description}
                      onChange={(e) => setEditingFactorData({ ...editingFactorData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                      rows={2}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Current Value</label>
                      <input
                        type="text"
                        placeholder="Current value"
                        value={editingFactorData.current_value}
                        onChange={(e) => setEditingFactorData({ ...editingFactorData, current_value: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Target Value</label>
                      <input
                        type="text"
                        placeholder="Target value"
                        value={editingFactorData.target_value}
                        onChange={(e) => setEditingFactorData({ ...editingFactorData, target_value: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Trend Direction</label>
                    <select
                      value={editingFactorData.kpi_type}
                      onChange={(e) => setEditingFactorData({ ...editingFactorData, kpi_type: e.target.value as 'increase' | 'decrease' })}
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                    >
                      <option value="increase">Increase (Higher is Better)</option>
                      <option value="decrease">Decrease (Lower is Better)</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    onClick={handleCancelFactorEdit}
                    className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveFactor(editingFactor)}
                    disabled={!editingFactorData.name}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Combiner les deux : Current/Target Condition
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Visualize current state and target goals side by side with shared factors
          </p>
        </div>
        {!isEditingLocal ? (
          canEdit ? (
          <button
            onClick={handleEdit}
            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <EditIcon className="h-4 w-4 mr-2" />
            Edit
          </button>
          ) : null
        ) : (
          <div className="flex space-x-2">
            <button
              onClick={handleCancel}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <XIcon className="h-4 w-4 mr-2" />
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Combined Current/Target View */}
      <div className="space-y-8">
        {outcomes.map((outcome, outcomeIndex) => (
          <div key={outcome.id} className="bg-gray-50 rounded-lg p-6">
            {/* Outcome Title */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center space-x-4 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">
                  {outcome.name}
                </h3>
                {isEditingLocal && (
                  <button
                    onClick={() => handleEditOutcome(outcome)}
                    className="p-1 text-blue-500 hover:bg-blue-100 rounded"
                    title="Edit outcome values"
                  >
                    <EditIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
              {outcome.description && (
                <p className="text-sm text-gray-600">{outcome.description}</p>
              )}
              
              {/* Outcome Value Editing */}
              {editingOutcome === outcome.id ? (
                <div className="mt-4 p-4 bg-white rounded-lg border border-blue-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Edit Outcome Values</h4>
                  <div className="grid grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Current Value</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Current value"
                        value={editingOutcomeData.current_value}
                        onChange={(e) => setEditingOutcomeData({ ...editingOutcomeData, current_value: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Target Value</label>
                      <input
                        type="number"
                        step="0.01"
                        placeholder="Target value"
                        value={editingOutcomeData.target_value}
                        onChange={(e) => setEditingOutcomeData({ ...editingOutcomeData, target_value: e.target.value })}
                        className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={handleCancelOutcomeEdit}
                      className="px-3 py-1 text-xs text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSaveOutcome(outcome.id)}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                isEditingLocal && (
                  <button
                    onClick={() => setShowAddFactor(outcome.id)}
                    className="mt-2 inline-flex items-center px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add Factor
                  </button>
                )
              )}
            </div>

            {/* Side by Side Current/Target */}
            <div className="flex gap-6 items-center">
              {/* Current Condition */}
              {renderOutcomeSection(outcome, outcomeIndex, false)}
              
              {/* Arrow */}
              <div className="flex-shrink-0">
                <ArrowRightIcon className="h-8 w-8 text-gray-400" />
              </div>
              
              {/* Target Condition */}
              {renderOutcomeSection(outcome, outcomeIndex, true)}
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {outcomes.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <TargetIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Outcomes Available
          </h3>
          <p className="text-gray-500 mb-4">
            Outcomes need to be created in Component 1 (Project Charter) first.
            <br />
            Once outcomes are defined there, you can add factors here to visualize current and target conditions.
          </p>
        </div>
      )}
      
      {/* Custom Alert Component */}
      {AlertComponent}
    </div>
  );
};

export default Component02_03_CurrentTargetCondition;
